import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/** Inner-page header: left-aligned title and lede, ruled off below. */
export function PageIntro({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <section className="border-border border-b">
      <div className="mx-auto max-w-5xl px-5 pt-14 pb-12 md:px-8 md:pt-20 md:pb-16">
        <h1 className="text-fluid-h1 text-navy max-w-4xl">{title}</h1>
        {children && <div className="text-fluid-body-lg text-muted-foreground mt-6 max-w-2xl">{children}</div>}
      </div>
    </section>
  )
}

/**
 * A titled section: the heading sits in a narrow left column on wide screens
 * and the body reads to its right, so every section on a page shares one edge.
 */
export function Section({
  title,
  children,
  tinted = false,
  className,
}: {
  title: string
  children: ReactNode
  tinted?: boolean
  className?: string
}) {
  return (
    <section className={cn(tinted ? 'bg-muted' : 'border-border border-b last:border-b-0', className)}>
      <div className="mx-auto grid max-w-5xl gap-6 px-5 py-14 md:grid-cols-[14rem_1fr] md:gap-12 md:px-8 md:py-20">
        <h2 className="text-fluid-h3 text-navy">{title}</h2>
        <div className="min-w-0">{children}</div>
      </div>
    </section>
  )
}

/** Label / value rows — for property facts and figures. */
export function FactList({
  rows,
  className,
}: {
  rows: ReadonlyArray<readonly [string, ReactNode]>
  className?: string
}) {
  return (
    <dl className={cn('tabular border-navy border-t', className)}>
      {rows.map(([k, v]) => (
        <div key={k} className="border-border flex justify-between gap-6 border-b py-3">
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="text-right font-medium">{v}</dd>
        </div>
      ))}
    </dl>
  )
}
